export type CalendarProviderId = "google" | "microsoft";

/**
 * `default` is a pointer — "use my global default meeting link" — resolved at booking time against
 * the calendar the user currently writes to. `google_meet` / `microsoft_teams` are what it can
 * resolve TO; they are never stored on an event type, because a Meet link can only be created on a
 * Google calendar and a Teams link only on a Microsoft one, so a frozen value would break the day
 * the user changed calendars.
 */
export type ConferencingProviderId =
  | "default"
  | "zoom"
  | "google_meet"
  | "microsoft_teams"
  | "custom_url"
  | "none";

/** The choices an event type may actually store: a pointer, or a provider-independent override. */
export type StorableConferencingProviderId = "default" | "zoom" | "custom_url" | "none";

export type IntegrationProviderId = CalendarProviderId | ConferencingProviderId;

export function toCanonicalProviderId(provider: string): string {
  return provider.trim().toLowerCase().replace(/-/g, "_");
}

export function toRouteProviderToken(provider: string): string {
  return toCanonicalProviderId(provider);
}

export function toCalendarProviderEnum(provider: CalendarProviderId): "GOOGLE" | "MICROSOFT" {
  return provider === "microsoft" ? "MICROSOFT" : "GOOGLE";
}

export function toConferencingProviderEnum(
  provider: ConferencingProviderId,
): "DEFAULT" | "ZOOM" | "GOOGLE_MEET" | "MICROSOFT_TEAMS" | "CUSTOM_URL" | "NONE" {
  switch (provider) {
    case "default":
      return "DEFAULT";
    case "google_meet":
      return "GOOGLE_MEET";
    case "microsoft_teams":
      return "MICROSOFT_TEAMS";
    case "custom_url":
      return "CUSTOM_URL";
    case "none":
      return "NONE";
    default:
      return "ZOOM";
  }
}

/** How a resolved default reads on screen: "My default — Google Meet". */
export function toConferencingLabel(provider: string | null | undefined): string {
  switch (toCanonicalProviderId(provider ?? "")) {
    case "google_meet":
      return "Google Meet";
    case "microsoft_teams":
      return "Microsoft Teams";
    case "zoom":
      return "Zoom";
    case "custom_url":
      return "a custom link";
    default:
      return "no meeting link";
  }
}

/**
 * The provider as a standalone value — what a "Where" field on a booking should read.
 *
 * <p>Distinct from {@link toConferencingLabel}, whose wording ("a custom link", "no meeting link")
 * is written to sit inside a sentence and reads oddly on its own line. This one names the thing:
 * "Google Meet", "Zoom", "Custom link", "In person".
 *
 * <p>NONE means no meeting link was minted, which for a booking that still has to happen somewhere
 * is an in-person meeting rather than an absence.
 */
export function toLocationLabel(provider: string | null | undefined): string {
  switch (toCanonicalProviderId(provider ?? "")) {
    case "google_meet":
    // A calendar provider is accepted as a fallback for older rows whose conferencing platform
    // was never recorded: a link minted on a Google calendar is a Meet link, one on a Microsoft
    // calendar a Teams link. The API sends the conferencing platform itself now, so these only
    // catch history.
    case "google":
    case "google_calendar":
      return "Google Meet";
    case "microsoft_teams":
    case "microsoft":
    case "microsoft_calendar":
      return "Microsoft Teams";
    case "zoom":
      return "Zoom";
    case "custom_url":
      return "Custom link";
    case "none":
      return "In person";
    default:
      return "";
  }
}

export function isOAuthConferencingProvider(provider: string): boolean {
  const canonical = toCanonicalProviderId(provider);
  return canonical === "zoom" || canonical === "google_meet" || canonical === "microsoft_teams";
}

export function toManagedProviderLabel(provider: string | null | undefined): string {
  const canonical = provider ? toCanonicalProviderId(provider) : "";
  if (canonical === "google_calendar" || canonical === "google") return "Google Calendar";
  if (canonical === "microsoft_calendar" || canonical === "microsoft") return "Microsoft Calendar";
  return canonical
    .split("_")
    .filter(Boolean)
    .map((part) => part[0].toUpperCase() + part.slice(1))
    .join(" ");
}

/**
 * What a public booking page's Location line should read, or "" to leave the line out entirely.
 *
 * <p>Order matters. The resolved conferencing platform wins: an event type stores "use my default
 * meeting link" rather than a platform, so the API follows that pointer and sends the answer.
 * ROUND_ROBIN sends nothing at all -- its writer is the member assigned after the guest picks, so
 * no platform is knowable yet, and naming the owner's own default would promise a guest a call
 * they may never join.
 *
 * <p>Only then the host's own words, and only when they are worth showing. `location` is free text
 * for the "no video call" case -- an office address, a phone number -- but older rows saved before
 * the editor started storing "" carry placeholder junk instead ("default", "Online", "TBD"), which
 * says nothing a guest could act on and reads as a bug on a round-robin page that is meant to stay
 * quiet. Those are dropped rather than migrated: the column is still live for real addresses, so a
 * blanket UPDATE would erase whatever genuine text a host had typed.
 */
const PLACEHOLDER_LOCATIONS = new Set(["default", "online", "n/a", "na", "tbd", "-", "--"]);

export function bookingLocationLine(
  conferencingProvider: string | null | undefined,
  location: string | null | undefined,
): string {
  const platform = toLocationLabel(conferencingProvider);
  if (platform) return platform;
  const text = (location ?? "").trim();
  if (!text || PLACEHOLDER_LOCATIONS.has(text.toLowerCase())) return "";
  return text;
}
